import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Moon, Sun } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import BrandWordmark from "../common/BrandWordmark";

interface PublicLegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

const PublicLegalLayout: React.FC<PublicLegalLayoutProps> = ({
  title,
  lastUpdated,
  children,
}) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div
      className={`min-h-screen ${
        isDark ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"
      }`}
    >
      <header
        className={`sticky top-0 z-50 border-b backdrop-blur-sm ${
          isDark
            ? "border-gray-800 bg-gray-900/95"
            : "border-gray-200 bg-white/95"
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="group flex items-center gap-2.5 transition-transform duration-300 hover:scale-[1.02]"
          >
            {isDark ? (
              <img
                src="/logo1.png"
                alt="AetherFlow Logo"
                className="w-10 h-10 sm:w-11 sm:h-11 shrink-0"
              />
            ) : (
              <img
                src="/logo0.png"
                alt="AetherFlow Logo"
                className="w-10 h-10 sm:w-11 sm:h-11 shrink-0"
              />
            )}
            <BrandWordmark theme={theme} variant="marketing" />
          </Link>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link
              to="/"
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isDark
                  ? "text-gray-300 hover:text-white hover:bg-gray-800"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-10">
          <p
            className={`text-sm font-medium uppercase tracking-wider mb-2 ${
              isDark ? "text-cyan-400" : "text-blue-600"
            }`}
          >
            Legal
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            {title}
          </h1>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Last updated: {lastUpdated}
          </p>
        </div>

        <div
          className={`rounded-2xl border p-6 sm:p-10 shadow-sm ${
            isDark
              ? "border-gray-800 bg-gray-800/50"
              : "border-gray-200 bg-white"
          }`}
        >
          {children}
        </div>
      </main>

      <footer
        className={`border-t py-8 text-center text-sm ${
          isDark
            ? "border-gray-800 text-gray-500"
            : "border-gray-200 text-gray-500"
        }`}
      >
        <p>© {new Date().getFullYear()} AetherFlow Systems. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PublicLegalLayout;
